/**
 * Admin data access.
 *
 * Types mirror what `apps/api/src/admin/admin.controller.ts` returns. They are
 * hand-written rather than generated because there are four of them and a
 * generator is a build step nobody asked for; if this grows past a dozen it is
 * worth revisiting.
 *
 * Every mutation invalidates the queries it can affect. Flipping the rollout
 * switch changes the brand summary too, so both refetch — a stale mode on the
 * brands tab would be a lie about whether the agent is live.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';

/**
 * The two positions of the per-brand rollout switch. `assisted` is retired
 * (Deivydas 2026-09-18) — the server refuses it and old runs are the only
 * place it survives.
 */
export type RolloutMode = 'shadow' | 'auto';

export interface BrandRow {
  id: string;
  slug: string;
  name: string;
  active: boolean;
  refundPolicy: Record<string, unknown>;
  commerceCredential: {
    envName: string;
    configured: boolean;
    /** Which one is actually in force. `env` wins over `stored`. */
    source: 'env' | 'stored' | null;
    /** Last four of the STORED key. Never the whole thing. */
    last4: string | null;
  };
  /** The brand's Iterable project key (marketing unsubscribes). Same shape. */
  marketingCredential: {
    envName: string;
    configured: boolean;
    source: 'env' | 'stored' | null;
    last4: string | null;
  };
  /** The brand's Suppression Hub code; null = matched by name. */
  suppressionHubCode: string | null;
  zendeskBrandId: string | null;
  storeUrl: string | null;
  /** UltraCart order-id prefix ("PHR1") — the cross-brand redirect reads it. */
  orderIdPrefix: string | null;
  /** Used by workflows that name the brand's support address. */
  supportEmail: string | null;
  /** The signature's phone line. */
  supportPhoneUs: string | null;
  supportPhoneIntl: string | null;
  /** The product catalogue's code for this brand, e.g. "PHR". */
  productApiCode: string | null;
  mode: RolloutMode;
  memberCount: number;
  canAdminister: boolean;
}

export interface RolloutRow {
  brandId: string;
  brandSlug: string;
  brandName: string;
  active: boolean;
  mode: RolloutMode;
  canPromote: boolean;
}

export interface ModelRow {
  id: string;
  role: string;
  provider: string;
  model: string;
  active: boolean;
  /**
   * Micro-dollars per million tokens. Null means unpriced — a run against this
   * model records its tokens and a null cost rather than a zero.
   */
  inputCostMicroPerMTok: number | null;
  outputCostMicroPerMTok: number | null;
  /** Whether the provider's API key is set on the server. Presence only. */
  keyPresent?: boolean;
}

export interface PersonRow {
  id: string;
  email: string;
  name: string | null;
  active: boolean;
  lastSeenAt: string | null;
  memberships: Array<{ brandId: string; brandSlug: string; brandName: string; role: string }>;
}

export const useBrands = () =>
  useQuery({ queryKey: ['admin', 'brands'], queryFn: () => apiGet<BrandRow[]>('/api/admin/brands') });

export const useRollout = () =>
  useQuery({
    queryKey: ['admin', 'rollout'],
    queryFn: () => apiGet<RolloutRow[]>('/api/admin/rollout'),
  });

export const useModels = () =>
  useQuery({ queryKey: ['admin', 'models'], queryFn: () => apiGet<ModelRow[]>('/api/admin/models') });

export const usePeople = () =>
  useQuery({ queryKey: ['admin', 'people'], queryFn: () => apiGet<PersonRow[]>('/api/admin/people') });

function useAdminMutation<TVariables>(
  run: (variables: TVariables) => Promise<unknown>,
  invalidate: string[][],
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: async () => {
      await Promise.all(invalidate.map((key) => client.invalidateQueries({ queryKey: key })));
    },
  });
}

export const usePromote = () =>
  useAdminMutation<{ brandId: string; mode: RolloutMode }>(
    (body) => apiPatch('/api/admin/rollout', body),
    [
      ['admin', 'rollout'],
      ['admin', 'brands'],
    ],
  );

export const useUpdateBrand = () =>
  useAdminMutation<{
    id: string;
    active?: boolean;
    refundPolicy?: unknown;
    /**
     * Write-only. The server never sends it back, so omitting the field is the
     * only way to leave it alone, and `''` is the only way to clear it.
     */
    ultracartKey?: string;
    /** Write-only, like the UltraCart key. `''` clears it. */
    iterableKey?: string;
    /** `''` clears it. */
    suppressionHubCode?: string;
    zendeskBrandId?: string;
    /** `''` clears it. */
    storeUrl?: string;
    /** UltraCart order-id prefix, e.g. "PHR1". `''` clears it. */
    orderIdPrefix?: string;
    /** These four: `''` clears. */
    supportEmail?: string;
    supportPhoneUs?: string;
    supportPhoneIntl?: string;
    productApiCode?: string;
  }>(
    ({ id, ...body }) => apiPatch(`/api/admin/brands/${id}`, body),
    [['admin', 'brands']],
  );

/** Superadmin only. A new brand starts in shadow; see `POST /admin/brands`. */
export interface NewBrand {
  name: string;
  slug: string;
  zendeskBrandId: string;
  guaranteeDays: number;
  ultracartKey?: string;
  storeUrl?: string;
  orderIdPrefix?: string;
  supportEmail?: string;
  supportPhoneUs?: string;
  supportPhoneIntl?: string;
  productApiCode?: string;
}

export const useCreateBrand = () =>
  useAdminMutation<NewBrand>(
    (body) => apiPost('/api/admin/brands', body),
    [
      ['admin', 'brands'],
      ['admin', 'rollout'],
    ],
  );

export const useUpdateModel = () =>
  useAdminMutation<{
    id: string;
    model?: string;
    active?: boolean;
    inputCostMicroPerMTok?: number | null;
    outputCostMicroPerMTok?: number | null;
  }>(
    ({ id, ...body }) => apiPatch(`/api/admin/models/${id}`, body),
    // A price change moves what the dashboard reports, so the analytics query
    // has to go stale alongside the model list.
    [
      ['admin', 'models'],
      ['analytics'],
    ],
  );

/** One role on every brand, or no access (`null`) — Deivydas 2026-10-16. */
export const useSetRole = () =>
  useAdminMutation<{ userId: string; role: string | null }>(
    ({ userId, role }) => apiPut(`/api/admin/people/${userId}/role`, { role }),
    [['admin', 'people']],
  );

export const useGrant = () =>
  useAdminMutation<{ userId: string; brandId: string; role: string }>(
    ({ userId, ...body }) => apiPut(`/api/admin/people/${userId}/memberships`, body),
    [['admin', 'people']],
  );

export const useRevoke = () =>
  useAdminMutation<{ userId: string; brandId: string }>(
    ({ userId, brandId }) => apiDelete(`/api/admin/people/${userId}/memberships/${brandId}`),
    [['admin', 'people']],
  );

/**
 * Removing is delete-or-deactivate, decided by the server: an account with no
 * recorded history is deleted outright, one with history is deactivated so the
 * audit trail keeps its attribution. The result says which happened.
 */
export const useRemoveUser = () =>
  useAdminMutation<{ userId: string }>(
    ({ userId }) => apiDelete(`/api/admin/people/${userId}`),
    [['admin', 'people']],
  );

export const useRestoreUser = () =>
  useAdminMutation<{ userId: string }>(
    ({ userId }) => apiPost(`/api/admin/people/${userId}/restore`, {}),
    [['admin', 'people']],
  );

export const useInvite = () =>
  useAdminMutation<{ email: string; name?: string; role: string; brandIds?: string[] }>(
    (body) => apiPost('/api/admin/people', body),
    [['admin', 'people']],
  );

export interface FollowUpSettings {
  enabled: boolean;
  /** Quiet hours after Zoye's last reply, 1–168. */
  hours: number;
  /** Nudges per quiet spell, 1–3. */
  max: number;
}

export interface AppConfig {
  replyDelayMinutes: number;
  /** The quiet-customer follow-up (2026-10-15). */
  followUp: FollowUpSettings;
  /** Company addresses Zoye answers like customers (2026-10-20). */
  internalTesters: string[];
  /** ISO moment of the last dashboard wipe, or null if never wiped. */
  dashboardDataFloor: string | null;
  /** Lifetime wipes, newest first — read from the audit trail. */
  wipeouts: Array<{ at: string; by: string }>;
}

export const useAppConfig = () =>
  useQuery({
    queryKey: ['admin', 'config'],
    queryFn: () => apiGet<AppConfig>('/api/admin/config'),
  });

export const useUpdateAppConfig = () =>
  useAdminMutation<{
    replyDelayMinutes?: number;
    followUp?: Partial<FollowUpSettings>;
    internalTesters?: string[];
  }>(
    (body) => apiPatch('/api/admin/config', body),
    [['admin', 'config']],
  );

/**
 * Hide everything currently on the dashboard (superadmin only). Nothing is
 * deleted — the server stores a floor timestamp the report queries filter
 * by — so every reports query goes stale along with the config.
 */
export const useWipeDashboard = () =>
  useAdminMutation<Record<string, never>>(
    () => apiPost('/api/admin/config/wipe-dashboard', {}),
    [
      ['admin', 'config'],
      ['reports'],
    ],
  );
